import { AbstractService, DeinitSource, DeinitMode, InternalEventBus, RawStorageKey, ApplicationEvent, } from '@standardnotes/services';
import { UuidGenerator } from '@standardnotes/utils';
import { ApplicationGroupEvent } from './ApplicationGroupEvent';
import { c } from 'ttag';
export class SNApplicationGroup extends AbstractService {
    constructor(device, internalEventBus) {
        if (internalEventBus === undefined) {
            internalEventBus = new InternalEventBus();
        }
        super(internalEventBus);
        this.device = device;
        this.onApplicationDeinit = (application, mode, source) => {
            if (this.primaryApplication === application) {
                ;
                this.primaryApplication = undefined;
            }
            const performSyncronously = async () => {
                if (source === DeinitSource.SignOut) {
                    void this.removeDescriptor(this.descriptorForApplication(application));
                }
                const descriptors = this.getDescriptors();
                if (descriptors.length === 0 || source === DeinitSource.SignOutAll) {
                    const identifiers = descriptors.map((d) => d.identifier);
                    this.descriptorRecord = {};
                    const { killsApplication } = await this.device.clearAllDataFromDevice(identifiers);
                    if (killsApplication) {
                        return;
                    }
                }
                const device = this.device;
                void this.notifyEvent(ApplicationGroupEvent.DeviceWillRestart, { source, mode });
                this.deinit();
                if (mode === DeinitMode.Hard) {
                    device.performHardReset();
                }
                else {
                    device.performSoftReset();
                }
            };
            void performSyncronously();
        };
    }
    deinit() {
        super.deinit();
        this.device.deinit();
        this.device = undefined;
        this.callback = undefined;
        this.primaryApplication = undefined;
        this.onApplicationDeinit = undefined;
    }
    async initialize(callback) {
        if (this.device.isDeviceDestroyed()) {
            throw 'Attempting to initialize new application while device is destroyed.';
        }
        this.callback = callback;
        this.descriptorRecord = (await this.device.getJsonParsedRawStorageValue(RawStorageKey.DescriptorRecord));
        if (!this.descriptorRecord) {
            await this.createNewDescriptorRecord();
        }
        let primaryDescriptor = this.findPrimaryDescriptor();
        if (!primaryDescriptor) {
            console.error('No primary application descriptor found. Ensure migrations have been run.');
            primaryDescriptor = this.getDescriptors()[0];
            this.setDescriptorAsPrimary(primaryDescriptor);
            await this.persistDescriptors();
        }
        const application = await this.buildApplication(primaryDescriptor);
        this.primaryApplication = application;
        application.addEventObserver(async () => {
            this.renameDescriptor(primaryDescriptor, application.sessions.getWorkspaceDisplayIdentifier());
        }, ApplicationEvent.SignedIn);
        await this.notifyEvent(ApplicationGroupEvent.PrimaryApplicationSet, { application: application });
    }
    async createNewDescriptorRecord() {
        /**
         * The identifier 'standardnotes' is used because this was the
         * database name of Standard Notes web/desktop
         * */
        const identifier = 'standardnotes';
        const descriptorRecord = {
            [identifier]: {
                identifier: identifier,
                label: c('B1.Account.Session.Label').t `Main Workspace`,
                primary: true,
            },
        };
        void this.device.setRawStorageValue(RawStorageKey.DescriptorRecord, JSON.stringify(descriptorRecord));
        this.descriptorRecord = descriptorRecord;
        await this.persistDescriptors();
    }
    getDescriptors() {
        return Object.values(this.descriptorRecord);
    }
    findPrimaryDescriptor() {
        for (const descriptor of this.getDescriptors()) {
            if (descriptor.primary) {
                return descriptor;
            }
        }
        return undefined;
    }
    async signOutAllWorkspaces() {
        await this.primaryApplication.user.signOut(false, DeinitSource.SignOutAll);
    }
    setDescriptorAsPrimary(primaryDescriptor) {
        for (const descriptor of this.getDescriptors()) {
            descriptor.primary = descriptor === primaryDescriptor;
        }
    }
    async persistDescriptors() {
        await this.device.setRawStorageValue(RawStorageKey.DescriptorRecord, JSON.stringify(this.descriptorRecord));
        void this.notifyEvent(ApplicationGroupEvent.DescriptorsDataChanged, { descriptors: this.descriptorRecord });
    }
    renameDescriptor(descriptor, label) {
        descriptor.label = label;
        void this.persistDescriptors();
    }
    removeDescriptor(descriptor) {
        delete this.descriptorRecord[descriptor.identifier];
        const descriptors = this.getDescriptors();
        if (descriptor.primary && descriptors.length > 0) {
            this.setDescriptorAsPrimary(descriptors[0]);
        }
        return this.persistDescriptors();
    }
    removeAllDescriptors() {
        this.descriptorRecord = {};
        return this.persistDescriptors();
    }
    descriptorForApplication(application) {
        return this.descriptorRecord[application.identifier];
    }
    createNewApplicationDescriptor(label) {
        const identifier = UuidGenerator.GenerateUuid();
        const index = this.getDescriptors().length + 1;
        const descriptor = {
            identifier: identifier,
            label: label || `Workspace ${index}`,
            primary: false,
        };
        return descriptor;
    }
    async createNewPrimaryDescriptor(label) {
        const descriptor = this.createNewApplicationDescriptor(label);
        this.descriptorRecord[descriptor.identifier] = descriptor;
        this.setDescriptorAsPrimary(descriptor);
        await this.persistDescriptors();
    }
    async unloadCurrentAndCreateNewDescriptor(label) {
        await this.createNewPrimaryDescriptor(label);
        if (this.primaryApplication) {
            this.primaryApplication.deinit(this.primaryApplication.getDeinitMode(), DeinitSource.SwitchWorkspace);
        }
    }
    async unloadCurrentAndActivateDescriptor(descriptor) {
        this.setDescriptorAsPrimary(descriptor);
        await this.persistDescriptors();
        if (this.primaryApplication) {
            this.primaryApplication.deinit(this.primaryApplication.getDeinitMode(), DeinitSource.SwitchWorkspace);
        }
    }
    async buildApplication(descriptor) {
        const application = await this.callback.applicationCreator(descriptor, this.device);
        application.setOnDeinit(this.onApplicationDeinit);
        return application;
    }
}
